import React, { useState, useEffect } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, AlertCircle } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";

export default function Downloads() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentVenue) return;
    setLoading(true);
    hosys.entities.Document.filter({ venue_id: currentVenue.id })
      .then(docs => setDocuments(docs.sort((a, b) => new Date(b.updated_date) - new Date(a.updated_date))))
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  }, [currentVenue?.id]);

  if (venueLoading || loading) return <QuickLoader />;

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Descarregues" 
        subtitle="Documents disponibles per descarregar"
      />

      {documents.length === 0 ? (
        <Card className="border-0 shadow-sm p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-400" />
            </div>
          </div>
          <p className="text-gray-500 font-medium">No hi ha documents disponibles</p>
          <p className="text-xs text-gray-400 mt-1">Els documents que generes apareixeran aquí</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {documents.map(doc => (
            <Card key={doc.id} className="border-0 shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{doc.name || "Document sense títol"}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {new Date(doc.updated_date).toLocaleDateString('ca-ES', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => window.open(doc.file_url, '_blank')}
                  size="sm"
                  className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white gap-2 flex-shrink-0"
                >
                  <Download className="w-4 h-4" />
                  Descarregar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-950">Documents generats automàticament</p>
          <p className="text-xs text-blue-800 mt-0.5">Els informes i documents que generes des de diferents pàgines apareixeran aquí per a descarregar en qualsevol moment.</p>
        </div>
      </div>
    </div>
  );
}